import { 
  Zap, 
  Shield, 
  TrendingDown, 
  Battery, 
  Sun, 
  ArrowRight, 
  CheckCircle2,
  BarChart3,
  Activity,
  Building2,
  Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';

export const Home = ({ onContactClick }) => {
  return (
    <main className="pt-16 lg:pt-20">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-50 to-white overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwMDAwMDAiIGZpbGwtb3BhY2l0eT0iMC4wMiI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
        
        <div className="container-main relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center py-16 lg:py-24">
            {/* Hero Content */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-full px-4 py-2">
                <Battery className="w-4 h-4 text-[#10B981]" />
                <span className="text-sm font-medium text-[#10B981]">Batteriespeicher für Krankenhäuser</span>
              </div>
              
              <h1 className="heading-xl text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Energiekosten senken. <br />
                <span className="text-[#10B981]">Versorgung sichern.</span>
              </h1>
              
              <p className="text-lg text-slate-600 max-w-xl leading-relaxed">
                Ein Battery Energy Storage System (BESS) reduziert Ihre Stromkosten um bis zu 30%, 
                erhöht die Versorgungssicherheit und macht Ihr Krankenhaus unabhängiger vom Stromnetz.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={onContactClick}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="hero-cta-btn"
                >
                  Kostenlose Potenzialanalyse
                  <ArrowRight className="w-5 h-5" />
                </Button>
                <Link to="/wirtschaftlichkeit">
                  <Button variant="outline" className="btn-secondary w-full sm:w-auto" data-testid="hero-secondary-btn">
                    Wirtschaftlichkeit berechnen
                  </Button>
                </Link>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center gap-6 pt-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                  <span className="text-sm text-slate-600">TÜV-zertifiziert</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                  <span className="text-sm text-slate-600">Made in Germany</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                  <span className="text-sm text-slate-600">10+ Jahre Garantie</span>
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative lg:col-span-1">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_hospital-bess-losung/artifacts/vzjaqok6_Hospital_BESS10.png"
                  alt="BESS-Anlage vor modernem Krankenhaus"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Stats Overlay */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-6 border border-slate-100">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center">
                    <TrendingDown className="w-6 h-6 text-[#10B981]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>-30%</p>
                    <p className="text-sm text-slate-500">Energiekosten</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="section-padding bg-white" data-testid="problem-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center mb-12 lg:mb-16">
            <h2 className="heading-lg text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Die Energieherausforderung im Krankenhaus
            </h2>
            <p className="text-lg text-slate-600">
              Krankenhäuser sind energieintensive Einrichtungen mit besonderen Anforderungen an Versorgungssicherheit und Kosteneffizienz.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            <Card className="card-feature" data-testid="problem-card-1">
              <CardContent className="p-6 lg:p-8">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mb-6">
                  <TrendingDown className="w-6 h-6 text-red-500 rotate-180" />
                </div>
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Steigende Stromkosten
                </h3>
                <p className="text-slate-600">
                  Energiepreise steigen kontinuierlich. Lastspitzen verursachen hohe Netzentgelte und belasten das Budget.
                </p>
              </CardContent>
            </Card>

            <Card className="card-feature" data-testid="problem-card-2">
              <CardContent className="p-6 lg:p-8">
                <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center mb-6">
                  <Zap className="w-6 h-6 text-amber-500" />
                </div>
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Netzinstabilität
                </h3>
                <p className="text-slate-600">
                  Schwankende Netzqualität gefährdet kritische Systeme. Ausfälle können lebenswichtige Geräte beeinträchtigen.
                </p>
              </CardContent>
            </Card>

            <Card className="card-feature" data-testid="problem-card-3">
              <CardContent className="p-6 lg:p-8">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                  <Sun className="w-6 h-6 text-blue-500" />
                </div>
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Ungenutzte PV-Potenziale
                </h3>
                <p className="text-slate-600">
                  Ohne Speicher wird eigenerzeugter Solarstrom oft ungenutzt ins Netz eingespeist statt selbst verbraucht.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="section-padding bg-slate-50" data-testid="solution-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <span className="text-[#10B981] font-medium text-sm uppercase tracking-wide">Die Lösung</span>
              <h2 className="heading-lg text-[#0F172A] mt-2 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Battery Energy Storage System (BESS)
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Ein BESS ist ein modulares Batteriespeichersystem, das Energie zwischenspeichert und bei Bedarf 
                wieder abgibt. Es optimiert Ihren Energiebezug, reduziert Lastspitzen und sichert kritische Versorgung.
              </p>

              <div className="space-y-4">
                {[
                  { icon: TrendingDown, title: 'Peak Shaving', desc: 'Kappung von Lastspitzen zur Reduzierung der Netzentgelte' },
                  { icon: Shield, title: 'Notstromversorgung', desc: 'Unterbrechungsfreie Versorgung kritischer Systeme' },
                  { icon: Sun, title: 'PV-Optimierung', desc: 'Maximale Eigenverbrauchsquote durch intelligente Speicherung' },
                  { icon: BarChart3, title: 'Arbitrage', desc: 'Nutzung günstiger Stromtarife durch zeitversetzten Bezug' },
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[#10B981]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h4>
                      <p className="text-sm text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_hospital-bess-losung/artifacts/93lshuia_Hospital_BESS3.png"
                  alt="Krankenhausgelände mit Solaranlagen und BESS"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Economic Value Section */}
      <section className="section-padding bg-white" data-testid="economics-section">
        <div className="container-main">
          {/* Savings Breakdown */}
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="card-feature text-center">
              <CardContent className="p-6 lg:p-8">
                <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Activity className="w-8 h-8 text-[#10B981]" />
                </div>
                <h4 className="text-lg font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Peak Shaving
                </h4>
                <p className="text-3xl font-bold text-[#10B981] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  8-15%
                </p>
                <p className="text-sm text-slate-500">Einsparung bei Netzentgelten</p>
              </CardContent>
            </Card>

            <Card className="card-feature text-center">
              <CardContent className="p-6 lg:p-8">
                <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sun className="w-8 h-8 text-[#10B981]" />
                </div>
                <h4 className="text-lg font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Eigenverbrauch
                </h4>
                <p className="text-3xl font-bold text-[#10B981] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  bis 90%
                </p>
                <p className="text-sm text-slate-500">bei vorhandener PV-Anlage</p>
              </CardContent>
            </Card>

            <Card className="card-feature text-center">
              <CardContent className="p-6 lg:p-8">
                <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="w-8 h-8 text-[#10B981]" />
                </div>
                <h4 className="text-lg font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Arbitrage
                </h4>
                <p className="text-3xl font-bold text-[#10B981] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  3-8%
                </p>
                <p className="text-sm text-slate-500">durch Tarifoptimierung</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-10">
            <Link to="/wirtschaftlichkeit">
              <Button variant="outline" className="btn-secondary inline-flex items-center gap-2" data-testid="economics-link-btn">
                Alle Einsparpotenziale ansehen
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Full-width Image Section */}
      <section className="w-full" data-testid="image-divider">
        <div className="w-full h-[300px] lg:h-[400px] overflow-hidden">
          <img
            src="https://customer-assets.emergentagent.com/job_hospital-bess-losung/artifacts/hskr0dbt_Hospital_BESS8.png"
            alt="Medizinisches Team vor modernem Krankenhaus"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 70%' }}
          />
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-slate-50" data-testid="process-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center mb-12 lg:mb-16">
            <span className="text-[#10B981] font-medium text-sm uppercase tracking-wide">Der Prozess</span>
            <h2 className="heading-lg text-[#0F172A] mt-2 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Von der Analyse zur Installation
            </h2>
            <p className="text-lg text-slate-600">
              Ein strukturierter Prozess für maximale Planungssicherheit und termingerechte Umsetzung.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Potenzialanalyse', desc: 'Analyse Ihres Lastprofils und Ermittlung des optimalen Speicherkonzepts', duration: '2-4 Wochen', icon: BarChart3 },
              { step: '02', title: 'Planung', desc: 'Detaillierte technische Planung und Wirtschaftlichkeitsberechnung', duration: '4-6 Wochen', icon: Building2 },
              { step: '03', title: 'Installation', desc: 'Schlüsselfertige Errichtung durch zertifizierte Fachpartner', duration: '6-10 Wochen', icon: Battery },
              { step: '04', title: 'Betrieb', desc: 'Inbetriebnahme, Monitoring und laufende Optimierung', duration: 'Dauerhaft', icon: Activity },
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-6 border border-slate-100 h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-4xl font-bold text-slate-100" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.step}</span>
                    <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-[#10B981]" />
                    </div>
                  </div>
                  <h4 className="text-lg font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {item.title}
                  </h4>
                  <p className="text-sm text-slate-600 mb-4">{item.desc}</p>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <Clock className="w-4 h-4" />
                    <span>{item.duration}</span>
                  </div>
                </div>
                {index < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-slate-200"></div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/prozess">
              <Button variant="outline" className="btn-secondary inline-flex items-center gap-2" data-testid="process-link-btn">
                Prozess im Detail
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Act Now Section */}
      <section className="section-padding bg-[#0F172A] text-white" data-testid="why-now-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="heading-lg mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Warum jetzt handeln?
            </h2>
            <p className="text-lg text-slate-300">
              Die wirtschaftlichen Rahmenbedingungen für Batteriespeicher waren nie günstiger.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Steigende Strompreise', desc: 'Je früher die Investition, desto schneller die Amortisation bei steigenden Energiekosten.' },
              { title: 'Förderprogramme', desc: 'Aktuelle Förderprogramme von Bund und Ländern verbessern die Wirtschaftlichkeit erheblich.' },
              { title: 'Netzentgelt-Reform', desc: 'Neue Regelungen belohnen Lastglättung und machen Peak Shaving noch attraktiver.' },
              { title: 'Technologiereife', desc: 'Batterietechnologie ist heute ausgereift, sicher und wirtschaftlich wie nie zuvor.' },
            ].map((item, index) => (
              <div key={index} className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h4 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {item.title}
                </h4>
                <p className="text-sm text-slate-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section-padding bg-gradient-to-br from-emerald-50 to-white" data-testid="cta-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="heading-lg text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Bereit für die nächste Stufe der Energieeffizienz?
            </h2>
            <p className="text-lg text-slate-600 mb-4">
              Lassen Sie uns gemeinsam Ihr individuelles Einsparpotenzial ermitteln. 
              Unverbindlich und kostenlos.
            </p>
            <p className="text-sm text-slate-500 mb-8">
              Erste Projekte in Umsetzung. Gerne stellen wir Ihnen konkrete Beispiele im Gespräch vor.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={onContactClick}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="final-cta-btn"
              >
                Kostenlose Erstberatung
                <ArrowRight className="w-5 h-5" />
              </Button>
              <Link to="/faq">
                <Button variant="outline" className="btn-secondary w-full sm:w-auto" data-testid="faq-link-btn">
                  Häufige Fragen
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
